import React, { useMemo } from 'react';
import { Page, Layout, Card, BlockStack, InlineGrid, Text, Avatar, Badge, Button, ProgressBar, Box, Divider, InlineStack } from '@shopify/polaris';
import { WalletIcon, OrderIcon, ChartVerticalFilledIcon, CalendarIcon, StarFilledIcon } from '@shopify/polaris-icons';
import { ReactECharts, ReactEChartsProps } from 'components/React-ECharts';
import { Helmet } from 'react-helmet-async';

// Fake Data
const ACCOUNT_DATA = {
  id: 'AFF-2023-8899',
  name: 'Nguyễn Văn An',
  email: 'an.nguyen@example.com',
  avatar: 'https://i.pravatar.cc/150?u=AFF-2023-8899',
  joinDate: '2023-05-15',
  level: 'Gold Partner',
  nextLevel: 'Diamond Partner',
  progressToNextLevel: 75, // percentage
  wallet: {
    balance: 15450000,
    pending: 2300000,
    currency: 'VND',
  },
  orders: {
    total: 1250,
    thisMonth: 45,
    pending: 5,
    completed: 38,
    cancelled: 2,
  },
  earnings: {
    total: 156000000,
    thisMonth: 8500000,
    lastMonth: 7200000,
  },
  chartData: {
    months: ['Thg 6', 'Thg 7', 'Thg 8', 'Thg 9', 'Thg 10', 'Thg 11'],
    earnings: [5000000, 6200000, 5800000, 7500000, 7200000, 8500000],
    orders: [25, 32, 28, 40, 38, 45],
  },
};

export default function AgencyMe() {
  // Helpers
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const calculateTenure = (dateString: string) => {
    const start = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays} ngày`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `${months} tháng`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return remainingMonths > 0 ? `${years} năm ${remainingMonths} tháng` : `${years} năm`;
  };

  // Chart Options
  const earningsChartOption: ReactEChartsProps['option'] = useMemo(
    () => ({
      title: {
        text: 'Thu nhập & Đơn hàng (6 tháng)',
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal' },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: {
        data: ['Thu nhập', 'Đơn hàng'],
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          data: ACCOUNT_DATA.chartData.months,
          axisPointer: { type: 'shadow' },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Thu nhập',
          min: 0,
          axisLabel: { formatter: '{value} đ' },
        },
        {
          type: 'value',
          name: 'Đơn hàng',
          min: 0,
          position: 'right',
          axisLabel: { formatter: '{value} đơn' },
        },
      ],
      series: [
        {
          name: 'Thu nhập',
          type: 'bar',
          tooltip: { valueFormatter: (value: any) => formatCurrency(value as number) },
          data: ACCOUNT_DATA.chartData.earnings,
          itemStyle: { color: '#008060' }, // Shopify Green
        },
        {
          name: 'Đơn hàng',
          type: 'line',
          yAxisIndex: 1,
          tooltip: { valueFormatter: (value: any) => value + ' đơn' },
          data: ACCOUNT_DATA.chartData.orders,
          itemStyle: { color: '#005bd3' }, // Shopify Blue
        },
      ],
    }),
    []
  );

  const orderStatusChartOption: ReactEChartsProps['option'] = useMemo(
    () => ({
      title: {
        text: 'Tỷ lệ đơn hàng tháng này',
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal' },
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        bottom: 0,
        left: 'center',
      },
      series: [
        {
          name: 'Trạng thái',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: [
            { value: ACCOUNT_DATA.orders.completed, name: 'Hoàn thành', itemStyle: { color: '#008060' } },
            { value: ACCOUNT_DATA.orders.pending, name: 'Đang xử lý', itemStyle: { color: '#e49400' } },
            { value: ACCOUNT_DATA.orders.cancelled, name: 'Đã hủy', itemStyle: { color: '#d72c0d' } },
          ],
        },
      ],
    }),
    []
  );

  return (
    <Page title="Tổng quan tài khoản" fullWidth>
      <Helmet>
        <title>Tổng quan tài khoản Affiliate</title>
      </Helmet>

      <Layout>
        {/* Top Section: User Profile & General Stats */}
        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <InlineGrid columns={{ xs: 1, sm: '1fr auto' }} gap="400" alignItems="center">
                <InlineStack gap="400" align="start" blockAlign="center">
                  <Avatar source={ACCOUNT_DATA.avatar} size="lg" name={ACCOUNT_DATA.name} />
                  <BlockStack gap="100">
                    <Text variant="headingLg" as="h2">
                      {ACCOUNT_DATA.name}
                    </Text>
                    <InlineStack gap="200" align="start" blockAlign="center">
                      <Badge tone="success">{ACCOUNT_DATA.level}</Badge>
                      <Text variant="bodySm" as="span" tone="subdued">
                        ID: {ACCOUNT_DATA.id}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </InlineStack>
                <InlineStack gap="400">
                  <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                    <BlockStack align="center" inlineAlign="center">
                      <Text variant="bodySm" as="p" tone="subdued">
                        Thâm niên
                      </Text>
                      <InlineStack gap="100" align="center">
                        <CalendarIcon width={16} />
                        <Text variant="bodyMd" as="strong" fontWeight="bold">
                          {calculateTenure(ACCOUNT_DATA.joinDate)}
                        </Text>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </InlineStack>
              </InlineGrid>

              <Divider />

              {/* Level Progress */}
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="bodySm" as="span">
                    Cấp độ hiện tại: <strong>{ACCOUNT_DATA.level}</strong>
                  </Text>
                  <Text variant="bodySm" as="span">
                    Tiếp theo: <strong>{ACCOUNT_DATA.nextLevel}</strong>
                  </Text>
                </InlineStack>
                <ProgressBar progress={ACCOUNT_DATA.progressToNextLevel} tone="primary" size="small" />
                <Text variant="bodyXs" as="p" tone="subdued">
                  Bạn cần thêm 25% doanh số để đạt cấp độ tiếp theo.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Key Metrics Cards */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Số dư ví
                  </Text>
                  <WalletIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {formatCurrency(ACCOUNT_DATA.wallet.balance)}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Đang chờ duyệt: {formatCurrency(ACCOUNT_DATA.wallet.pending)}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Thu nhập tháng này
                  </Text>
                  <ChartVerticalFilledIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {formatCurrency(ACCOUNT_DATA.earnings.thisMonth)}
                </Text>
                <InlineStack gap="100">
                  <Text variant="bodySm" as="span" tone="success">
                    ▲ 18%
                  </Text>
                  <Text variant="bodySm" as="span" tone="subdued">
                    so với tháng trước
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Đơn hàng tháng này
                  </Text>
                  <OrderIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {ACCOUNT_DATA.orders.thisMonth}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Tổng cộng: {ACCOUNT_DATA.orders.total} đơn từ khi tham gia
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Charts Section */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: '2fr 1fr' }} gap="400">
            <Card>
              <ReactECharts option={earningsChartOption} style={{ height: '350px', width: '100%' }} />
            </Card>
            <Card>
              <ReactECharts option={orderStatusChartOption} style={{ height: '350px', width: '100%' }} />
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Additional Info / Tips */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="200" align="start">
                <StarFilledIcon width={24} />
                <Text variant="headingMd" as="h3">
                  Mẹo tăng thu nhập
                </Text>
              </InlineStack>
              <BlockStack gap="200">
                <Text as="p">
                  🎉 <strong>Chiến dịch tháng 12</strong> đang diễn ra! Chia sẻ các sản phẩm thuộc danh mục "Thời trang" để nhận thêm 5% hoa hồng.
                </Text>
                <Text as="p">💡 Cập nhật thông tin tài khoản ngân hàng để đảm bảo thanh toán đúng hạn vào ngày 15 hàng tháng.</Text>
                <Button variant="plain" textAlign="left">
                  Xem chi tiết chính sách hoa hồng &rarr;
                </Button>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

